import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { FileText, Target, TrendingUp, BookOpen, ArrowRight, Clock } from "lucide-react";
import { RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, ResponsiveContainer, Tooltip } from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/dashboard`).then(r => { setData(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="p-8 lg:p-12 animate-fade-in">
        <div className="h-8 w-64 bg-secondary rounded animate-pulse mb-8" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {[1,2,3,4].map(i => <div key={i} className="h-32 bg-secondary rounded animate-pulse" />)}
        </div>
      </div>
    );
  }

  const stats = [
    { label: "Tests Taken", value: data?.total_tests || 0, icon: FileText, color: "text-indigo-400" },
    { label: "Avg Score", value: `${data?.avg_score || 0}%`, icon: Target, color: "text-emerald-400" },
    { label: "Best Score", value: `${data?.best_score || 0}%`, icon: TrendingUp, color: "text-amber-400" },
    { label: "Question Bank", value: data?.total_questions_in_bank || 0, icon: BookOpen, color: "text-pink-400" },
  ];

  const radarData = (data?.topic_performance || []).map(t => ({
    subject: t.topic_name.split(" ")[0],
    accuracy: t.accuracy,
    fullMark: 100,
  }));

  return (
    <div className="p-8 lg:p-12 space-y-8 animate-fade-in" data-testid="dashboard">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="font-heading text-4xl md:text-5xl font-bold tracking-tight">
            Command Centre
          </h1>
          <p className="text-muted-foreground mt-2 text-base">
            {data?.total_tests > 0
              ? `${data.total_tests} tests completed. Keep pushing.`
              : "Begin your preparation journey. Every question counts."}
          </p>
        </div>
        <Button
          data-testid="start-new-test-btn"
          onClick={() => navigate("/test/setup")}
          className="bg-primary hover:bg-primary/90 shadow-lg shadow-indigo-500/20 gap-2"
        >
          New Test <ArrowRight size={16} />
        </Button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((s, i) => (
          <Card
            key={s.label}
            className={`stat-card bg-card border-border opacity-0 animate-fade-in stagger-${i+1}`}
            data-testid={`stat-${s.label.toLowerCase().replace(/\s/g, "-")}`}
          >
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-3">
                <s.icon size={20} className={s.color} />
                <span className="text-xs text-muted-foreground uppercase tracking-widest">{s.label}</span>
              </div>
              <p className="font-mono text-3xl font-bold">{s.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Radar Chart */}
        <Card className="lg:col-span-5 bg-card border-border" data-testid="topic-radar-chart">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-lg">Topic Mastery</CardTitle>
            <p className="text-xs text-muted-foreground">Accuracy across subjects</p>
          </CardHeader>
          <CardContent>
            {radarData.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <RadarChart data={radarData}>
                  <PolarGrid stroke="hsl(var(--border))" />
                  <PolarAngleAxis dataKey="subject" tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }} />
                  <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 9 }} />
                  <Radar name="Accuracy" dataKey="accuracy" stroke="#6366f1" fill="#6366f1" fillOpacity={0.2} strokeWidth={2} />
                  <Tooltip contentStyle={{ background: "#121214", border: "1px solid #27272a", borderRadius: "6px", color: "#fafafa", fontSize: "12px" }} />
                </RadarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[280px] flex items-center justify-center text-muted-foreground text-sm">
                Take your first test to see topic mastery
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Tests */}
        <Card className="lg:col-span-7 bg-card border-border" data-testid="recent-tests">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-lg">Recent Tests</CardTitle>
          </CardHeader>
          <CardContent>
            {(data?.recent_tests || []).length > 0 ? (
              <div className="space-y-3">
                {data.recent_tests.map((t, i) => (
                  <div
                    key={i}
                    data-testid={`recent-test-${i}`}
                    className="flex items-center justify-between p-3 rounded-sm bg-secondary/30 hover:bg-secondary/50 transition-colors cursor-pointer"
                    onClick={() => navigate(`/test/${t.test_id}/results`)}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-sm bg-primary/10 flex items-center justify-center">
                        <FileText size={16} className="text-primary" />
                      </div>
                      <div>
                        <p className="text-sm font-medium">{t.test_code}</p>
                        <p className="text-xs text-muted-foreground flex items-center gap-1">
                          <Clock size={12} />
                          {t.submitted_at ? new Date(t.submitted_at).toLocaleDateString() : "N/A"}
                          <span className="mx-1">|</span>
                          {t.question_count} Q
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-mono text-lg font-bold">
                        {t.score?.percentage?.toFixed(1) || 0}%
                      </p>
                      <Badge
                        variant="outline"
                        className={`text-xs ${
                          (t.score?.percentage || 0) >= 60
                            ? "border-emerald-500/50 text-emerald-400"
                            : (t.score?.percentage || 0) >= 40
                            ? "border-amber-500/50 text-amber-400"
                            : "border-red-500/50 text-red-400"
                        }`}
                      >
                        {t.score?.correct || 0}/{t.score?.total || 0}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="h-[200px] flex flex-col items-center justify-center text-muted-foreground gap-3">
                <FileText size={32} className="opacity-50" />
                <p className="text-sm">No tests taken yet</p>
                <Button
                  data-testid="take-first-test-btn"
                  size="sm"
                  variant="outline"
                  onClick={() => navigate("/test/setup")}
                >
                  Take First Test
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
